import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { hashPin, isPlainPin } from './utils/crypto';
import * as Sentry from '@sentry/react';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import Login, { ROLES, AGENT_ROLE } from './components/Login';
import AccountSignIn from './components/AccountSignIn';
import AccountVerifyEmail, { ACCOUNT_VERIFY_EMAIL_PATH } from './components/AccountVerifyEmail';
import AccountResetPassword from './components/AccountResetPassword';
import SetupWizard from './components/SetupWizard';
import ResetStaffPin from './components/ResetStaffPin';
import { DEMO_MODE, getTenantConfig, syncOfflineSales, getWarehouses } from './services/api';
import { queueCounts, healAuthFailures, adoptUntaggedSales } from './services/offlineQueue';
import { T, S } from './styles/ds';
import { applyTenantResolution } from './utils/tenantResolution';
import ShopSwitcher, { ShopChangedElsewhere } from './components/ShopSwitcher';
import { nameKnownShop } from './utils/knownShops';
import { shopChangedElsewhere, classifyTenantChange, takeForcePinFlag, getWindowSlug, setWindowSlug } from './utils/shopSwitch';
import { ACCOUNT_RESET_PASSWORD_PATH, isPath } from './utils/accountLinkRoute';

// H-17: Route-level code splitting — each page loads only when first navigated to.
// Login, SetupWizard and ResetStaffPin stay eager (shown before auth, no route needed).
const StockIn    = lazy(() => import('./pages/StockIn'));
const StockOut   = lazy(() => import('./pages/StockOut'));
const ReturnsTab = lazy(() => import('./pages/ReturnsTabPage'));
const StockTake  = lazy(() => import('./pages/StockTake'));
const Analytics  = lazy(() => import('./pages/Analytics'));
const Sales      = lazy(() => import('./pages/Sales'));
const Udhar      = lazy(() => import('./pages/Udhar'));
const Followup   = lazy(() => import('./pages/Followup'));
const Settings   = lazy(() => import('./pages/Settings'));
const OwnerPanel = lazy(() => import('./pages/OwnerPanel'));
const Customers  = lazy(() => import('./pages/Customers'));
const DayClose        = lazy(() => import('./pages/DayClose'));
const PurchaseOrders  = lazy(() => import('./pages/PurchaseOrders'));
const Compliance      = lazy(() => import('./pages/Compliance'));
const Expenses        = lazy(() => import('./pages/Expenses'));
const Assistant       = lazy(() => import('./pages/Assistant'));
const Schemes         = lazy(() => import('./pages/Schemes'));
const SaltSearch      = lazy(() => import('./pages/SaltSearch'));
const TakeOrder       = lazy(() => import('./pages/TakeOrder'));
const Claims          = lazy(() => import('./pages/Claims'));
const Collections     = lazy(() => import('./pages/Collections'));
const FieldCollection = lazy(() => import('./pages/FieldCollection'));
const ReceivedCreditNotes = lazy(() => import('./pages/ReceivedCreditNotes'));

// Visual nav groups — static section headers behind a segmented switcher (not collapsible, by design:
// hiding daily actions raises "where is it?" support cost). Ordered by real daily frequency (PH-60).
export const NAV_GROUPS = [
  { id: 'daily', label: 'रोज़ का काम · Daily' },
  { id: 'stock', label: 'दुकान / माल · Stock' },
  { id: 'owner', label: 'हिसाब / प्रबंधन · Manage' },
];

// Emoji per nav-group for the group switcher (keyed by NAV_GROUPS id).
const GROUP_ICON = { daily: '🌞', stock: '📦', owner: '💼' };

// Items ordered by frequency within each group. The `group` id must exist in NAV_GROUPS — the nav
// renders one group at a time, so a wrong id makes an item silently unreachable (PH-60).
export const NAV_ITEMS = [
  // Daily (रोज़ का काम)
  { path: '/stock-out', icon: '🛒', label: 'Make Sale (बिक्री)',                    group: 'daily',  roles: ['admin', 'sales'] },
  { path: '/salt-search', icon: '💊', label: 'Salt Search (साल्ट खोज)',             group: 'daily',  roles: ['admin', 'sales'] },
  { path: '/stocktake', icon: '📋', label: 'Stock Count (गिनती)',                   group: 'daily',  roles: ['admin', 'counter'] },
  { path: '/udhar',     icon: '📒', label: 'Credit (उधार)',                          group: 'daily',  roles: ['admin', 'sales'] },
  { path: '/returns',   icon: '🔄', label: 'Returns (वापसी)',                        group: 'daily',  roles: ['admin', 'sales'] },
  { path: '/followup',  icon: '📞', label: 'Follow-up (याद दिलाएं)',                 group: 'daily',  roles: ['admin', 'sales'] },
  { path: '/customers', icon: '🧑', label: 'Customers (ग्राहक)',                     group: 'daily',  roles: ['admin'] },
  { path: '/day-close', icon: '🔒', label: 'Day Close (दिन बंद)',                    group: 'daily',  roles: ['admin', 'sales'] },
  { path: '/take-order', icon: '🛎️', label: 'Take Order (ऑर्डर)',                   group: 'daily',  roles: ['admin', 'sales'], module: 'wholesale' },
  // Shop & stock (दुकान / माल)
  { path: '/',          icon: '📦', label: 'Stock In (माल प्राप्ति)',    end: true, group: 'stock',  roles: ['admin'] },
  { path: '/sales',     icon: '🧾', label: 'Sales History (बिक्री)',                group: 'stock',  roles: ['admin', 'sales'] },
  // Accounts & owner (हिसाब / प्रबंधन)
  { path: '/analytics', icon: '📈', label: 'Analytics (रिपोर्ट)',                   group: 'owner',  roles: ['admin'] },
  { path: '/assistant', icon: '✨', label: 'Sahayak (सहायक)',                       group: 'owner',  roles: ['admin', 'sales'] },
  { path: '/purchase-orders', icon: '📑', label: 'Purchase Orders (खरीद)',            group: 'owner',  roles: ['admin'] },
  { path: '/received-credit-notes', icon: '🧮', label: 'Credit Notes (क्रेडिट नोट)',  group: 'owner',  roles: ['admin'] },
  { path: '/expenses',  icon: '💸', label: 'Expenses (खर्च)',                        group: 'owner',  roles: ['admin'] },
  { path: '/compliance', icon: '℞', label: 'Compliance (अनुपालन)',                  group: 'owner',  roles: ['admin'], module: 'retail' },
  { path: '/schemes',   icon: '🎁', label: 'Schemes (योजनाएं)',                     group: 'owner',  roles: ['admin'], module: 'wholesale' },
  { path: '/claims',    icon: '🎯', label: 'Claims (क्लेम)',                        group: 'owner',  roles: ['admin'], module: 'wholesale' },
  { path: '/field-collection', icon: '💼', label: 'Field Collection (वसूली)',       group: 'owner',  roles: ['admin'], module: 'wholesale' },
  { path: '/collections', icon: '💼', label: 'Collections (वसूली)',                 group: 'owner',  roles: ['agent'], module: 'wholesale' },
  { path: '/settings',  icon: '⚙️', label: 'Settings (सेटिंग्स)',                   group: 'owner',  roles: ['admin'] },
];

// Visible-nav grouping helpers — exported for the PH-60 regression test. Grouping is additive UI,
// never an access change: the union of every group's items must equal the flat role+module filter.
//
// `role` accepts either a single role string (every pre-PH-47 caller, and the PH-60 test) or an
// array of roles (PH-47's per-person login, which can hold more than one job at once — Codex PR
// review finding, real: filtering by only the single, priority-collapsed `sub` role hid a route
// the person's OTHER job legitimately unlocks, e.g. Sales+Store Keeper never saw Stock Count).
export function visibleNavFor(role, enabledModules) {
  const roleSet = Array.isArray(role) ? role : [role];
  return NAV_ITEMS.filter(item => item.roles.some(r => roleSet.includes(r)) && (!item.module || enabledModules[item.module]));
}

// PH-67 — the offline-sale drain runs only with someone signed in and the device online. Replayed
// behind the PIN screen, the owner sign-in or the setup wizard, every sale POST answers 401.
// This is a guard, not the fix: a session that looks live here can still 401 (its cookie expired
// or was revoked), and offlineQueue.js keeps the sales queued in that case on its own.
export function shouldDrainOfflineQueue(role, online) {
  return Boolean(role) && online === true;
}

export function buildNavGroups(visibleNav) {
  return NAV_GROUPS
    .map(g => ({ ...g, items: visibleNav.filter(i => i.group === g.id) }))
    .filter(g => g.items.length > 0);
}

export function resolveCurrentGroup(navGroups, activeGroup) {
  return navGroups.some(g => g.id === activeGroup) ? activeGroup : (navGroups[0] && navGroups[0].id);
}

const DEFAULT_PATH = {
  admin:   '/stock-out',
  sales:   '/stock-out',
  counter: '/stocktake',
  agent:   '/collections',
};

const SIDEBAR_BG = T.sidebarGrad;   // ink→deep-ink gradient (Suvidha AI shell)
const SIDEBAR_W  = 220;

function buildRoles(config) {
  if (!config) return ROLES;
  // Use config.pins for the demo/local PIN hint when present; otherwise fall back to the imported ROLES
  // (a production config hydrated from the backend never carries PINs — they're server-side only). The
  // agent card must NOT depend on config.pins, or wholesale tenants would never see the agent login.
  const roles = config.pins
    ? {
        admin:   { ...ROLES.admin,   pin: config.pins.admin },
        sales:   { ...ROLES.sales,   pin: config.pins.sales },
        counter: { ...ROLES.counter, pin: config.pins.counter },
      }
    : { ...ROLES };
  // Collection-agent login only for wholesale/distributor tenants (dynamic per-agent PIN, not a role PIN).
  if (config.moduleWholesale) roles.agent = AGENT_ROLE;
  return roles;
}

// Pattern C multi-tenant (PH-49 extends it with a path-based install anchor — see
// utils/tenantResolution.js for why and for the actual resolution/wiring logic): resolve
// which shop this device/tab belongs to and apply it once at module load (before App
// renders). Module scope (not a useState initializer / effect) so it runs exactly once and
// is free of React render-purity concerns (StrictMode double-invoke, etc.) — critical here,
// since a stray second run could clear tenantConfig/session a beat after the first run
// already rendered with them.
//
// TENANT_SWITCHED_ON_LOAD (a PR-review finding, confirmed real): a switch used to be
// completely silent — nothing told the person standing at the till that THIS load just
// re-tenanted their device. That was already true for ?t= links; /s/<slug> makes it easier to
// trigger by accident, since a path reads as an ordinary page URL where a query param at
// least LOOKS like something that could carry a parameter. App() below reads this once (its
// useState initializer) and shows a one-time, dismissible notice on the pre-login screens.
// Reads `notifySwitch`, not `switched` — a second Codex PR-review finding, confirmed real:
// `switched` alone is true on a brand-new device or the exact clear-site-data-then-relaunch
// this ticket exists to fix (nothing WAS "switched" there — there was no previous shop),
// which would have shown "this device now points to a different shop" on the very success
// path PH-49 exists to prove works. `notifySwitch` additionally requires a genuinely
// different, previously-known tenant — see tenantResolution.js.
// If applyTenantResolution() throws (a third PR-review finding — a localStorage write can
// fail: private browsing, a full quota) the flag correctly stays false, not by accident:
// tenantResolution.js writes the new slug FIRST, before clearing anything, so a throw there
// means the switch never actually landed — the device stayed on its old tenant, and showing
// "you switched shops" in that case would be actively wrong, not just missing.
// PH-47 / ADR-010 — shared by the initial load AND refreshTenantConfig() below (adversarial
// review finding, real: a device's cached tenantConfig never re-fetched meant every OTHER till
// in a shop kept showing the old shared-PIN screen forever after a different device ran the
// forced cutover, since staffLoginEnabled was only ever read from localStorage). One function so
// both call sites build the exact same shape.
function tenantConfigFromApiResponse(data) {
  return {
    shopName: data.shop_name,
    isConfigured: true,
    recoveryPhone: data.recovery_phone || '',
    recoveryEmail: data.recovery_email || '',
    gstin: data.gstin || '',
    shopAddress: data.shop_address || '',
    stateCode: data.state_code || '',
    upiVpa: data.upi_vpa || '',
    allowOversell:    data.allow_oversell     || false,
    enablePriceTiers: data.enable_price_tiers || false,
    chaseEnabled:     data.chase_enabled      || false,
    moduleRetail:     data.module_retail !== false,   // default true (old cached configs / missing field)
    moduleWholesale:  data.module_wholesale  || false,
    staffLoginEnabled: data.staff_login_enabled || false,
  };
}

let TENANT_SWITCHED_ON_LOAD = false;
// PH-50 — which shop THIS window loaded for (utils/shopSwitch.js keeps it: setWindowSlug below).
// Every other window on the device shares the same localStorage and login cookie, so when one of
// them points the device at a different shop, this is how this window notices it is no longer the
// shop on screen (see the `storage` listener in App below). Falls back to whatever is stored if
// resolution itself threw.
// PH-50 — set when this window was reloaded because another window switched shops: the session
// saved on the device belongs to that other window's sign-in, so this window must not pick it up
// without a PIN. Read (and cleared) once at module load, never per render.
const FORCE_PIN_ON_LOAD = takeForcePinFlag();
(function resolveTenantAndManifest() {
  try {
    const resolved = applyTenantResolution();
    TENANT_SWITCHED_ON_LOAD = resolved.notifySwitch;
    setWindowSlug(resolved.slug);
  } catch (err) {
    try { setWindowSlug(localStorage.getItem('pos_tenant_id') || ''); } catch (_) { /* none */ }
    // Best-effort — falls back to whatever tenant was already in localStorage / the static
    // manifest. But "best-effort" silently is worse than useless here: a QuotaExceededError
    // (private browsing, full storage) means the tenant slug never got PERSISTED even though
    // the current page still resolved correctly — the device looks fine until the next reload,
    // then quietly loses its shop. Visible in devtools, same convention as index.js's own
    // service-worker registration failure, not a new pattern (a PR-review finding, confirmed
    // real: silence here is worse than usual for a multi-tenant identity path).
    console.warn('Tenant resolution failed:', err);
  }
})();

function App() {
  const [tenantConfig, setTenantConfig] = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [role, setRole] = useState(null);
  // PH-47 / ADR-010 — the full set of jobs a per-person login holds (already mapped to this
  // frontend's role vocabulary by Login.jsx), or null for every OTHER session shape (legacy role
  // PIN, agent, Owner Account) — those have exactly one role, same as before this ticket. `role`
  // itself stays the single "which face to default to" value (e.g. the initial landing route);
  // `jobs` is what nav/route visibility actually checks now, via `hasRole` below, so a person
  // holding more than one job is not hidden from routes their OTHER job legitimately unlocks.
  const [jobs, setJobs] = useState(null);
  const hasRole = useCallback((r) => (jobs ? jobs.includes(r) : role === r), [jobs, role]);
  const [warehouseId, setWarehouseId] = useState(null);
  const [warehouseName, setWarehouseName] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [accountSignIn, setAccountSignIn] = useState(false);
  // PH-69 — flipped once the owner has finished with the email-confirmation screen and asked to sign
  // in, so the confirmation branch below stops claiming the render and the normal pre-login flow
  // takes over. Without it, pressing "Sign in" would re-render the same screen for ever, because the
  // pathname it matches on does not change.
  const [verifyLinkDone, setVerifyLinkDone] = useState(false);
  // PH-68 — flipped once the owner has finished setting a new password and asked to sign in, so the
  // reset branch below stops claiming the render and the normal pre-login flow takes over.
  const [resetLinkDone, setResetLinkDone] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [shopSwitchNotice, setShopSwitchNotice] = useState(TENANT_SWITCHED_ON_LOAD);
  const [activeGroup, setActiveGroup] = useState(null);   // which nav-group tab is showing (null → first group)
  // PH-50 — another window on this device switched it to a different shop (see getWindowSlug).
  const [shopChangedElsewhereFlag, setShopChangedElsewhereFlag] = useState(false);

  useEffect(() => {
    const act = (verdict) => {
      if (verdict === 'adopt') {
        // The empty shop key was filled in with this window's own (default) shop — not a switch.
        try { setWindowSlug(localStorage.getItem('pos_tenant_id') || ''); } catch (_) { /* keep */ }
        return;
      }
      if (verdict !== 'changed') return;
      // Drop this window's signed-in state right away; the early return below then unmounts every
      // page, so nothing here can make one more call under the other shop's login.
      setRole(null);
      setJobs(null);
      setWarehouseId(null);
      setWarehouseName(null);
      setShopChangedElsewhereFlag(true);
    };
    const onStorage = (e) => act(shopChangedElsewhere(e, getWindowSlug()));
    // A second check whenever this window comes back into view, for browsers that do not deliver
    // `storage` events between installed app windows reliably (review finding, PH-50 round 1: iOS
    // home-screen apps). No old value is known here, so nothing is adopted on this path: a window
    // that missed a fill-in's event is stopped too — once, and safely — rather than let a switch by.
    const onFocus = () => {
      if (document.visibilityState === 'hidden') return;
      act(classifyTenantChange(getWindowSlug(), undefined));
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, []);

  // PH-50 — give the switcher list a readable name for this shop, once its config is known. Only
  // names a shop the list already holds (knownShops.nameKnownShop never adds one).
  useEffect(() => {
    if (!tenantConfig?.shopName || !getWindowSlug()) return;
    try { nameKnownShop(localStorage, getWindowSlug(), tenantConfig.shopName); } catch (_) { /* best-effort label */ }
  }, [tenantConfig]);

  useEffect(() => {
    const stored = localStorage.getItem('tenantConfig');
    if (stored) {
      try {
        const cfg = JSON.parse(stored);
        // Migrate: if any PIN is still stored as plain text, re-hash it now
        if (cfg.pins && Object.values(cfg.pins).some(isPlainPin)) {
          Promise.all(
            Object.entries(cfg.pins).map(async ([role, pin]) => [role, isPlainPin(pin) ? await hashPin(pin) : pin])
          ).then(entries => {
            cfg.pins = Object.fromEntries(entries);
            localStorage.setItem('tenantConfig', JSON.stringify(cfg));
            setTenantConfig({ ...cfg });
          });
        }
        // Migrate: add enablePriceTiers if absent (pre-dates the feature)
        if (cfg.enablePriceTiers === undefined) cfg.enablePriceTiers = false;
        setTenantConfig(cfg);
        document.title = cfg.shopName || 'Pharmacy OS';
      } catch (_) {}
      setConfigLoaded(true);
      return;
    }

    // No local config — check backend; if store is already set up, hydrate and show login
    getTenantConfig()
      .then(res => {
        if (res.data?.is_configured) {
          const cfg = tenantConfigFromApiResponse(res.data);
          localStorage.setItem('tenantConfig', JSON.stringify(cfg));
          setTenantConfig(cfg);
          document.title = cfg.shopName || 'Pharmacy OS';
        }
      })
      .catch(() => {}) // backend unreachable → falls through to SetupWizard
      .finally(() => setConfigLoaded(true));
  }, []);

  // PH-47 / ADR-010 — a device's `tenantConfig` is otherwise cached in localStorage forever and
  // never re-checked. Login.jsx calls this on a stale-config signal instead of just showing an
  // error, so this device picks up a config change (e.g. staffLoginEnabled) without being stuck
  // showing a dead screen forever (adversarial + standard review finding, both confirmed real).
  // PH-71 — the old shared-PIN login's own 403 {"code":"staff_login_required"} trigger for this
  // no longer exists (that whole login path is retired), but the refresh itself is still useful
  // defensively, so it stays wired into Login.jsx rather than being removed.
  const refreshTenantConfig = useCallback(() => {
    return getTenantConfig()
      .then(res => {
        if (res.data?.is_configured) {
          const cfg = tenantConfigFromApiResponse(res.data);
          localStorage.setItem('tenantConfig', JSON.stringify(cfg));
          setTenantConfig(cfg);
        }
      })
      .catch(() => {}); // best-effort — device stays on its current (stale) screen if unreachable
  }, []);

  useEffect(() => {
    if (role) return;
    // PH-50 — reloaded after another window switched shops: that session is the other window's.
    if (FORCE_PIN_ON_LOAD) return;
    try {
      const s = JSON.parse(localStorage.getItem('session') || '{}');
      if (s.role && s.expiry > Date.now()) {
        setRole(s.role);
        setJobs(Array.isArray(s.jobs) ? s.jobs : null);
        setWarehouseId(s.warehouse_id || null);
        setWarehouseName(s.warehouse_name || null);
      }
    } catch (_) {}
  }, [role]);

  // H-21: offline sale queue — badge state + sync on reconnect / app load.
  // useCallback keeps stable identities so the event handlers below never close
  // over a stale version.
  const [queueState, setQueueState] = useState({ queued: 0, failed: 0, otherStore: 0, unknownStore: 0 });
  const refreshQueueCounts = useCallback(() => {
    queueCounts().then(setQueueState).catch(() => {});
  }, []);
  // PH-67 — resolves once the app-load heal (below) has finished, so a drain never reads the
  // queue before the records an older build wrongly failed have been put back.
  const healDone = useRef(null);
  // PH-67 — the drain needs a live session: replayed with none, every sale POST answers 401.
  // `role` is in the deps on purpose: a PIN login sets it in place with no reload, so a new
  // attemptSync identity is what re-runs the drain effect below straight after sign-in.
  const attemptSync = useCallback(() => {
    if (!shouldDrainOfflineQueue(role, navigator.onLine)) return;
    Promise.resolve(healDone.current)
      // PH-67 — untagged (older-build) sales are released only into a one-store shop. Best-effort:
      // any failure leaves them held and the drain of this store's sales still runs.
      .then(() => Promise.resolve().then(() => adoptUntaggedSales(getWarehouses)).catch(() => {}))
      .then(() => syncOfflineSales())
      .then(() => refreshQueueCounts())
      .catch(() => refreshQueueCounts());
  }, [role, refreshQueueCounts]);

  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline  = () => { setIsOffline(false); attemptSync(); };
    window.addEventListener('offline', goOffline);
    window.addEventListener('online',  goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online',  goOnline);
    };
  }, [attemptSync]);

  useEffect(() => {
    // PH-67 — on app load, signed in or not: put back any sale an older build marked `failed`
    // only because nobody was signed in. Device-local storage only, no server call. Best-effort
    // and inside the promise chain, so a missing or broken IndexedDB never breaks the app.
    healDone.current = Promise.resolve()
      .then(() => healAuthFailures())
      .catch(() => {})
      .then(() => refreshQueueCounts());
    const onChange = () => refreshQueueCounts();
    window.addEventListener('offline-queue-changed', onChange);
    return () => window.removeEventListener('offline-queue-changed', onChange);
  }, [refreshQueueCounts]);

  // Drain anything left from a prior session — on load once a session is restored, and again
  // straight after a sign-in (attemptSync changes identity with `role`).
  useEffect(() => {
    attemptSync();
  }, [attemptSync]);

  // PH-67 — the badge splits waiting sales by store, so recount whenever the signed-in store
  // changes, even when no drain runs (offline, or nothing for this store).
  useEffect(() => {
    refreshQueueCounts();
  }, [role, warehouseId, refreshQueueCounts]);

  useEffect(() => {
    const handler = () => setUpdateAvailable(true);
    document.addEventListener('app-update-available', handler);
    return () => document.removeEventListener('app-update-available', handler);
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // H-14: Idle session timeout — logout after 30 min of no activity
  useEffect(() => {
    if (!role) return;
    const IDLE_MS = 30 * 60 * 1000; // 30 minutes
    let lastActivity = Date.now();
    const resetTimer = () => { lastActivity = Date.now(); };
    const OPTS = { passive: true };
    const WIN_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    WIN_EVENTS.forEach(e => window.addEventListener(e, resetTimer, OPTS));
    // visibilitychange fires when user returns to the tab — prevents spurious logout
    document.addEventListener('visibilitychange', resetTimer);
    const interval = setInterval(() => {
      if (Date.now() - lastActivity > IDLE_MS) {
        localStorage.removeItem('session');
        setRole(null);
        setJobs(null);
        setWarehouseId(null);
        setWarehouseName(null);
      }
    }, 60_000); // check every minute
    return () => {
      WIN_EVENTS.forEach(e => window.removeEventListener(e, resetTimer, OPTS));
      document.removeEventListener('visibilitychange', resetTimer);
      clearInterval(interval);
    };
  }, [role]);

  // PH-50 — first, above every other screen: this window's shop is no longer the device's shop.
  if (shopChangedElsewhereFlag) return <ShopChangedElsewhere />;

  // PH-69 — the screen the emailed Owner Account confirmation link lands on.
  //
  // Deliberately ABOVE `if (!configLoaded) return null;`, for the same reason as PH-68's reset
  // branch. A cold hard load of the emailed link on a device with no cached `tenantConfig` would
  // otherwise render nothing at all until an unrelated getTenantConfig() round-trip resolves — a
  // blank white page on a brand-new owner's very first interaction with the product. This screen
  // needs no tenant config, no session and no role, so it has nothing to wait for.
  //
  // It cannot be a <Route>: <Router> only wraps the post-login app, so every pre-login screen is an
  // early conditional return like this one. No nginx.conf change is needed either —
  // `try_files $uri $uri/ /index.html` already serves the app at this path.
  if (!verifyLinkDone && isPath(ACCOUNT_VERIFY_EMAIL_PATH, window.location.pathname)) {
    return (
      <AccountVerifyEmail
        onSignIn={() => {
          // Same stale-session hazard PH-68's adversarial review found on its own reset screen: the
          // mount effect above restores any staff/PIN/agent session from localStorage
          // unconditionally, before this screen even knows it exists. Without clearing it here,
          // `role` can already be truthy by the time this fires (a cashier used PIN login on this
          // same till earlier), so `if (!role)` further down never opens and "Sign in →" would
          // silently land in that stale session's app instead of the Owner Account sign-in screen
          // this button promises.
          localStorage.removeItem('session');
          setRole(null);
          setJobs(null);
          setWarehouseId(null);
          setWarehouseName(null);
          // Codex review finding on PR #70 (real, confirmed), the same class PH-68's reset screen
          // was fixed for in PR #69: AccountSignIn.finish() reloads the page on a successful sign-in.
          // `verifyLinkDone` is plain React state, so that reload resets it to false — if the address
          // bar were still `/account/verify-email`, this branch would fire again with the token
          // already scrubbed and show the tokenless "incomplete link" screen instead of the signed-in
          // app, and pressing its sign-in button would then clear the newly created session. Move off
          // this pathname before sign-in can reach that reload.
          window.history.replaceState(null, '', '/');
          setVerifyLinkDone(true);
          setAccountSignIn(true);
        }}
      />
    );
  }

  // PH-68 — the screen the emailed password-reset link lands on.
  //
  // Deliberately ABOVE `if (!configLoaded) return null;`, and NOT beside the `/owner` branch
  // further down. A cold hard load of the emailed link on a device with no cached `tenantConfig`
  // would otherwise render nothing at all until an unrelated getTenantConfig() round-trip resolves
  // — a blank white page on the one screen a locked-out owner was just sent to. This screen depends
  // on no tenant config, no session and no role, so it has nothing to wait for.
  //
  // It cannot be a <Route>: <Router> only wraps the post-login app, so every pre-login screen is an
  // early conditional return like this one. No nginx.conf change is needed either —
  // `try_files $uri $uri/ /index.html` already serves the app at this path.
  //
  // Nothing here touches the offline sale queue. App's mount effect already calls attemptSync() on
  // every load regardless of session, so this screen inherits exactly today's pre-login behaviour.
  if (!resetLinkDone && isPath(ACCOUNT_RESET_PASSWORD_PATH, window.location.pathname)) {
    return (
      <AccountResetPassword
        onSignIn={() => {
          // Review finding (adversarial pass, real): the mount effect above (line 278) restores
          // any stale staff/PIN/agent session from localStorage unconditionally, before this
          // screen even knows it exists. Without clearing it here, `role` can already be truthy
          // by the time this fires (e.g. a cashier used PIN login on this same till earlier), so
          // `if (!role)` below never opens and “Sign in →” silently lands back in that stale
          // session's app instead of the owner sign-in screen this button promises.
          localStorage.removeItem('session');
          setRole(null);
          setJobs(null);
          setWarehouseId(null);
          setWarehouseName(null);
          // Codex review finding on PR #68 (real, confirmed): AccountSignIn.finish() reloads the
          // page on a successful sign-in. `resetLinkDone` is plain React state, so that reload
          // resets it to false — if the address bar were still `/account/reset-password`, this
          // branch would fire again and show the tokenless "incomplete link" screen instead of the
          // signed-in app. Move off this pathname before sign-in can reach that reload.
          window.history.replaceState(null, '', '/');
          setResetLinkDone(true);
          setAccountSignIn(true);
        }}
      />
    );
  }

  if (!configLoaded) return null;

  // H-21: queued/failed offline-sale badge (shows whenever there are pending or failed sales)
  // PH-67: sales waiting for another store, or for no recorded store, are never sent from this
  // one — say so instead of counting them as "waiting to sync" here.
  const otherStore = queueState.otherStore || 0;
  const unknownStore = queueState.unknownStore || 0;
  const waitingHere = Math.max(0, queueState.queued - otherStore - unknownStore);
  const queueBadge = (queueState.queued > 0 || queueState.failed > 0) ? (
    <div style={{
      position: 'fixed', bottom: isOffline ? 36 : 0, left: 0, right: 0, zIndex: 9998,
      background: queueState.failed > 0 ? T.red.text : T.ink, color: '#fff',
      display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 8,
      padding: '7px 16px', fontSize: 12, fontWeight: 600,
      letterSpacing: '0.03em', fontFamily: T.font,
    }}>
      {waitingHere > 0 && (
        <span>📴 {waitingHere} बिक्री ऑफलाइन सेव — सिंक होने का इंतज़ार / {waitingHere} sale(s) saved offline, waiting to sync</span>
      )}
      {otherStore > 0 && (
        <span>🏪 {otherStore} बिक्री दूसरे स्टोर की है — उस स्टोर में लॉग इन करने पर भेजी जाएगी / {otherStore} sale(s) from another store — sent when you log in to that store</span>
      )}
      {unknownStore > 0 && (
        <span>⏸️ {unknownStore} बिक्री का स्टोर दर्ज नहीं — अपने आप नहीं भेजी जाएगी / {unknownStore} sale(s) with no store recorded — held, not sent automatically</span>
      )}
      {queueState.failed > 0 && (
        <span>⚠️ {queueState.failed} सिंक नहीं हो सकी / {queueState.failed} failed to sync — check Sales</span>
      )}
    </div>
  ) : null;

  const offlineBanner = (
    <>
      {isOffline ? (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999,
          background: T.ink, color: '#fff', textAlign: 'center',
          padding: '8px 16px', fontSize: 12, fontWeight: 600, letterSpacing: '0.04em',
          fontFamily: T.font,
        }}>
          ऑफलाइन / Offline — इंटरनेट कनेक्शन नहीं है
        </div>
      ) : null}
      {queueBadge}
    </>
  );

  // PH-49 — a tenant switch (a fresh ?t= or /s/<slug> link resolving to a DIFFERENT shop than
  // this device was already on) is otherwise silent. Shown once, only pre-login: by the time
  // someone reaches the till they've already confirmed the right shop by entering ITS PIN, so
  // repeating the notice past that point would be stale noise, not a safeguard.
  const shopSwitchBanner = shopSwitchNotice ? (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
      background: T.spark, color: T.ink, textAlign: 'center',
      padding: '8px 16px', fontSize: 12, fontWeight: 600, letterSpacing: '0.02em',
      fontFamily: T.font, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
      flexWrap: 'wrap',
    }}>
      <span>यह डिवाइस अब एक अलग दुकान से जुड़ा है / This device now points to a different shop</span>
      <button
        onClick={() => setShopSwitchNotice(false)}
        aria-label="बंद करें / Dismiss"
        style={{
          ...S.tap,
          background: 'transparent', border: 'none', color: T.ink, fontWeight: 700,
          fontSize: 14, lineHeight: 1, cursor: 'pointer', flexShrink: 0,
        }}
      >
        ×
      </button>
    </div>
  ) : null;

  // Hidden owner panel — bypasses shop auth entirely; no link in nav
  if (window.location.pathname === '/owner' || window.location.hash.startsWith('#/owner')) {
    return <><Suspense fallback={<div style={{ padding: 40, textAlign: 'center', color: T.sec, fontFamily: T.font }}>लोड हो रहा है... / Loading</div>}><OwnerPanel /></Suspense>{offlineBanner}</>;
  }

  if (!tenantConfig?.isConfigured) {
    return <><SetupWizard onComplete={cfg => setTenantConfig(cfg)} />{offlineBanner}{shopSwitchBanner}</>;
  }

  const roles = buildRoles(tenantConfig);
  const shopName = tenantConfig.shopName || 'Pharmacy OS';

  if (!role) {
    // PH-71 — the forced StaffSetup cutover screen is retired: every shop is created already on
    // per-person staff login (setup_tenant), so `tenantConfig.staffLoginEnabled` is always true
    // and there is no more "not yet cut over" state for that screen to have applied to. The
    // ordinary Login screen's own "Forgot PIN?" is now the only path into ResetStaffPin.
    if (resetting) {
      return (
        <>
          <ResetStaffPin
            config={tenantConfig}
            onComplete={() => { localStorage.removeItem('session'); setResetting(false); }}
            onCancel={() => setResetting(false)}
          />
          {offlineBanner}
          {shopSwitchBanner}
        </>
      );
    }
    if (accountSignIn) {
      return (
        <>
          <AccountSignIn
            shopName={shopName}
            onCancel={() => setAccountSignIn(false)}
          />
          {offlineBanner}
        </>
      );
    }
    return (
      <>
        <div style={{ position: 'absolute', top: shopSwitchNotice ? 52 : 12, right: 12, zIndex: 10 }}>
          <ShopSwitcher currentSlug={getWindowSlug()} />
        </div>
        <Login
          onLogin={(r, whId, whName, jobRoles) => { setRole(r); setJobs(jobRoles || null); setWarehouseId(whId || null); setWarehouseName(whName || null); setActiveGroup(null); }}
          demoMode={DEMO_MODE} roles={roles} shopName={shopName} onForgotPin={() => setResetting(true)}
          onAccountSignIn={() => setAccountSignIn(true)} staffLoginEnabled={tenantConfig.staffLoginEnabled}
          onStaffLoginRequired={refreshTenantConfig} moduleWholesale={tenantConfig.moduleWholesale}
        />
        {offlineBanner}
        {shopSwitchBanner}
      </>
    );
  }

  const roleInfo = roles[role];
  // Compose nav by BOTH role and enabled business modules (retail/wholesale). An item
  // with no `module` is shared (shown for every face); a tagged item shows only when
  // that module is on. Missing moduleRetail (old cached config) defaults to retail-on.
  const enabledModules = { retail: tenantConfig.moduleRetail !== false, wholesale: !!tenantConfig.moduleWholesale };
  const visibleNav = visibleNavFor(jobs || role, enabledModules);
  const defaultPath = DEFAULT_PATH[role];
  // Nav is organised into groups (रोज़ का काम / Stock / Manage); a segmented switcher shows one group
  // at a time. Only groups that have items for this role appear — an empty group never gets a tab.
  const navGroups = buildNavGroups(visibleNav);
  const currentGroup = resolveCurrentGroup(navGroups, activeGroup);

  if (resetting) {
    return (
      <>
        <ResetStaffPin
          config={tenantConfig}
          onComplete={() => { localStorage.removeItem('session'); setResetting(false); }}
          onCancel={() => setResetting(false)}
        />
        {offlineBanner}
      </>
    );
  }

  const sidebarNavLink = ({ isActive }) => ({
    display: 'block',
    textDecoration: 'none',
    padding: '10px 18px',
    paddingLeft: isActive ? 15 : 18,
    margin: '1px 0',
    fontSize: 13,
    fontWeight: isActive ? 600 : 500,
    letterSpacing: '-0.005em',
    transition: 'color 0.12s, background 0.12s',
    borderLeft: isActive ? `3px solid ${T.spark}` : '3px solid transparent',
    background: isActive ? 'rgba(231,179,106,0.10)' : 'transparent',
    color: isActive ? '#F4E6CD' : 'rgba(255,255,255,0.6)',
    fontFamily: T.font,
  });

  // Render the ACTIVE group's items only — the group switcher (in the sidebar header) picks the group.
  // Shared by the desktop sidebar and the mobile drawer.
  const renderNav = (onItemClick) => {
    const g = navGroups.find(x => x.id === currentGroup) || navGroups[0];
    if (!g) return null;
    return g.items.map(item => (
      <NavLink key={item.path} to={item.path} end={item.end} style={sidebarNavLink} onClick={onItemClick}>
        <span style={{ marginRight: 8 }}>{item.icon}</span>{item.label}
      </NavLink>
    ));
  };

  const sidebarFooter = (onLogout) => (
    <div style={{ padding: '14px 18px 18px', borderTop: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.55)',
          background: 'rgba(255,255,255,0.06)',
          padding: '3px 8px',
          borderRadius: 3,
          fontFamily: T.font,
        }}>
          {role}
        </span>
        {(() => {
          // PH-15: the name picked at login — shown beside the role so whoever is at the till can see
          // whose name their sales are being recorded under (and log out if it is not theirs).
          let staffName = null;
          try { staffName = JSON.parse(localStorage.getItem('session') || '{}').staff_name || null; } catch (_) { staffName = null; }
          return staffName ? (
            <span title="बिक्री इसी नाम से दर्ज हो रही है / Sales are recorded under this name" style={{
              fontSize: 11, fontWeight: 600, color: '#fff', background: 'rgba(255,255,255,0.10)',
              padding: '2px 8px', borderRadius: 3, fontFamily: T.font, maxWidth: '100%',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              👤 {staffName}
            </span>
          ) : null;
        })()}
        {DEMO_MODE && (
          <span style={{
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.55)',
            background: 'rgba(255,255,255,0.06)',
            padding: '3px 8px',
            borderRadius: 3,
            fontFamily: T.font,
          }}>
            Demo
          </span>
        )}
      </div>
      <button
        onClick={onLogout}
        style={{
          width: '100%',
          background: 'transparent',
          border: '1px solid rgba(255,255,255,0.12)',
          color: 'rgba(255,255,255,0.7)',
          fontSize: 12,
          fontWeight: 500,
          padding: '8px 10px',
          borderRadius: 4,
          cursor: 'pointer',
          fontFamily: T.font,
          letterSpacing: '-0.005em',
          transition: 'border-color 0.12s, color 0.12s',
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; e.currentTarget.style.color = '#fff'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
      >
        बाहर जाएँ / Sign Out
      </button>
    </div>
  );

  // Nav-group switcher — रोज़ का काम / Stock / Manage tabs (bilingual). Shown only when the current
  // role/module has more than one group; a single-group role never sees a one-tab switcher.
  const groupSwitcher = navGroups.length > 1 ? (
    <div style={{ marginTop: 14, display: 'flex', gap: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 11, padding: 3 }}>
      {navGroups.map(g => {
        const [hi, en] = g.label.split(' · ');
        const on = g.id === currentGroup;
        return (
          <button key={g.id} onClick={() => setActiveGroup(g.id)} title={en || hi} aria-pressed={on}
            style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                     padding: '7px 3px', border: 'none', borderRadius: 8, cursor: 'pointer', fontFamily: T.font,
                     background: on ? T.spark : 'transparent',
                     color: on ? T.ink : 'rgba(255,255,255,0.62)',
                     boxShadow: on ? '0 3px 9px -3px rgba(0,0,0,0.4)' : 'none',
                     transition: 'background 0.12s, color 0.12s' }}
            onMouseEnter={e => { if (!on) { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = '#fff'; } }}
            onMouseLeave={e => { if (!on) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.62)'; } }}>
            <span style={{ fontSize: 13, lineHeight: 1 }}>{GROUP_ICON[g.id] || '•'}</span>
            <span style={{ fontSize: 10.5, fontWeight: on ? 800 : 700, lineHeight: 1 }}>{hi}</span>
            <span style={{ fontSize: 8, fontWeight: 600, lineHeight: 1.05, textAlign: 'center', opacity: 0.8 }}>{en}</span>
          </button>
        );
      })}
    </div>
  ) : null;

  const sidebarHeader = (
    <div style={{ padding: '22px 18px 18px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
      <div style={{
        fontSize: 15,
        fontWeight: 700,
        color: '#fff',
        lineHeight: 1.25,
        letterSpacing: '-0.01em',
        fontFamily: T.font,
      }}>
        {shopName}
      </div>
      <ShopSwitcher currentSlug={getWindowSlug()} tone="dark" />
      {warehouseName && (
        <div style={{
          fontSize: 11,
          fontWeight: 500,
          color: 'rgba(255,255,255,0.45)',
          marginTop: 4,
          fontFamily: T.mono,
          letterSpacing: '0.01em',
        }}>
          {warehouseName}
        </div>
      )}
      {groupSwitcher}
    </div>
  );

  const handleLogout = () => { localStorage.removeItem('session'); setRole(null); setJobs(null); setWarehouseId(null); setWarehouseName(null); setActiveGroup(null); };

  return (
    <Router>
      <div style={{
        height: '100vh',
        display: 'flex',
        background: T.bg,
        overflow: 'hidden',
        fontFamily: T.font,
        color: T.pri,
      }}>

        {/* ── LEFT SIDEBAR (desktop) ── */}
        {!isMobile && (
          <aside data-surface="dark" style={{
            width: SIDEBAR_W,
            background: SIDEBAR_BG,
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
            height: '100vh',
          }}>
            {sidebarHeader}
            <nav style={{ flex: 1, padding: '12px 0', overflowY: 'auto' }}>
              {renderNav()}
            </nav>
            {sidebarFooter(handleLogout)}
          </aside>
        )}

        {/* ── MAIN AREA ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

          {/* Mobile topbar */}
          {isMobile && (
            <nav data-surface="dark" style={{
              background: SIDEBAR_BG,
              padding: '14px 18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}>
              <div>
                <div style={{
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 15,
                  letterSpacing: '-0.01em',
                  fontFamily: T.font,
                }}>
                  {shopName}
                </div>
                <ShopSwitcher currentSlug={getWindowSlug()} tone="dark" />
                {warehouseName && (
                  <div style={{
                    fontSize: 11,
                    fontWeight: 500,
                    color: 'rgba(255,255,255,0.45)',
                    marginTop: 2,
                    fontFamily: T.mono,
                  }}>
                    {warehouseName}
                  </div>
                )}
              </div>
              <button
                onClick={() => setMobileNavOpen(o => !o)}
                aria-label="Menu"
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  padding: '6px 12px',
                  lineHeight: 1,
                  borderRadius: 4,
                  fontFamily: T.font,
                  letterSpacing: '0.02em',
                }}
              >
                {mobileNavOpen ? 'Close' : 'Menu'}
              </button>
            </nav>
          )}

          {/* Mobile drawer (slides from left) */}
          {isMobile && mobileNavOpen && (
            <>
              <div
                onClick={() => setMobileNavOpen(false)}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 100 }}
              />
              <div data-surface="dark" style={{
                position: 'fixed',
                top: 0,
                left: 0,
                bottom: 0,
                width: 260,
                background: SIDEBAR_BG,
                zIndex: 101,
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
              }}>
                {sidebarHeader}
                <nav style={{ flex: 1, padding: '12px 0', overflowY: 'auto' }}>
                  {renderNav(() => setMobileNavOpen(false))}
                </nav>
                {sidebarFooter(() => { handleLogout(); setMobileNavOpen(false); })}
              </div>
            </>
          )}

          {/* Update banner */}
          {updateAvailable && (
            <div data-surface="dark" style={{
              background: T.ink,
              color: '#fff',
              textAlign: 'center',
              padding: '8px 16px',
              fontSize: 12,
              fontWeight: 600,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 14,
              fontFamily: T.font,
              letterSpacing: '0.01em',
            }}>
              <span>नया अपडेट / New version available</span>
              <button
                onClick={() => window.location.reload()}
                style={{
                  background: '#fff',
                  color: T.ink,
                  border: 'none',
                  borderRadius: 3,
                  padding: '4px 12px',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: T.font,
                }}
              >
                Refresh
              </button>
            </div>
          )}

          {/* Page content — lazy chunks load on first navigation */}
          <main style={{ flex: 1, overflowY: 'auto', padding: 24, background: T.bg }}>
            <Suspense fallback={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: T.sec, fontSize: 14, fontFamily: T.font }}>
                लोड हो रहा है... / Loading
              </div>
            }>
              <Routes>
                {hasRole('admin') && (
                  <>
                    <Route path="/" element={<StockIn role={role} />} />
                    <Route path="/analytics" element={<Analytics enablePriceTiers={tenantConfig.enablePriceTiers} />} />
                    <Route path="/purchase-orders" element={<PurchaseOrders role={role} />} />
                    <Route path="/received-credit-notes" element={<ReceivedCreditNotes />} />
                    <Route path="/schemes" element={enabledModules.wholesale ? <Schemes /> : <Navigate to={defaultPath} replace />} />
                    <Route path="/take-order" element={enabledModules.wholesale ? <TakeOrder /> : <Navigate to={defaultPath} replace />} />
                    <Route path="/claims" element={enabledModules.wholesale ? <Claims /> : <Navigate to={defaultPath} replace />} />
                    <Route path="/field-collection" element={enabledModules.wholesale ? <FieldCollection /> : <Navigate to={defaultPath} replace />} />
                    <Route path="/settings" element={<Settings config={tenantConfig} onUpdate={cfg => { setTenantConfig(cfg); document.title = cfg.shopName || 'Pharmacy OS'; }} onChangePins={() => setResetting(true)} />} />
                  </>
                )}
                {(hasRole('admin') || hasRole('sales')) && (
                  <>
                    <Route path="/stock-out" element={<StockOut tenantConfig={tenantConfig} />} />
                    <Route path="/assistant" element={<Assistant />} />
                    <Route path="/salt-search" element={<SaltSearch role={role} />} />
                    <Route path="/sales" element={<Sales role={role} />} />
                    <Route path="/udhar" element={<Udhar role={role} shopName={shopName} />} />
                    <Route path="/returns" element={<ReturnsTab />} />
                    <Route path="/followup"  element={<Followup />} />
                    <Route path="/customers" element={<Customers enablePriceTiers={tenantConfig.enablePriceTiers} />} />
                    <Route path="/day-close" element={<DayClose />} />
                    <Route path="/compliance" element={enabledModules.retail ? <Compliance /> : <Navigate to={defaultPath} replace />} />
                    <Route path="/expenses" element={<Expenses />} />
                  </>
                )}
                {(hasRole('admin') || hasRole('counter')) && (
                  <Route path="/stocktake" element={<StockTake role={role} />} />
                )}
                {hasRole('agent') && (
                  <Route path="/collections" element={<Collections />} />
                )}
                <Route path="*" element={<Navigate to={defaultPath} replace />} />
              </Routes>
            </Suspense>
          </main>
        </div>

        {offlineBanner}
      </div>
    </Router>
  );
}

export default Sentry.withErrorBoundary(App, {
  fallback: (
    <div style={{ padding: 40, textAlign: 'center', fontFamily: T.font, background: T.bg, minHeight: '100vh' }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: T.pri, margin: 0, letterSpacing: '-0.01em' }}>
        कुछ गड़बड़ हो गई / Something went wrong
      </h2>
      <p style={{ color: T.sec, fontSize: 13, marginTop: 8 }}>Please refresh the page to continue.</p>
      <button
        onClick={() => window.location.reload()}
        style={{
          marginTop: 18,
          padding: '8px 18px',
          background: T.ink,
          color: '#fff',
          border: 'none',
          borderRadius: 4,
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: T.font,
        }}
      >
        Refresh
      </button>
    </div>
  ),
});
